import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _CheckIcon, _SvgIcon;
var _excluded = ["checked", "count", "description", "disabled", "label", "onChange", "readOnly", "size", "type"];
import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CheckIcon from '@mui/icons-material/Check';
import MuiRadio from '@mui/material/Radio';
import SvgIcon from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var muiSize = {
  md: 'medium',
  sm: 'small'
};
var checkCircleSizeMap = {
  md: 20,
  sm: 16
};
export function Radio(props) {
  var _props$checked = props.checked,
    checked = _props$checked === void 0 ? false : _props$checked,
    count = props.count,
    description = props.description,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    label = props.label,
    onChange = props.onChange,
    _props$readOnly = props.readOnly,
    readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
    _props$size = props.size,
    size = _props$size === void 0 ? 'sm' : _props$size,
    type = props.type,
    others = _objectWithoutProperties(props, _excluded);
  var theme = useTheme();
  var isCheckCircle = type === 'check-circle' && checked;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Stack, {
      alignItems: "flex-start",
      direction: "row",
      gap: 1.5,
      sx: {
        '&:hover': {
          cursor: readOnly || disabled ? 'default' : 'pointer'
        },
        pointerEvents: readOnly || disabled ? 'none' : 'unset'
      },
      "data-element": "Stack",
      "data-source-file": "Radio.tsx",
      children: [/*#__PURE__*/_jsx(MuiRadio, _extends({
        checked: checked
      }, isCheckCircle && {
        checkedIcon: _CheckIcon || (_CheckIcon = /*#__PURE__*/_jsx(CheckIcon, {}))
      }, {
        className: readOnly ? 'Mui-readOnly' : '',
        disabled: disabled,
        icon: _SvgIcon || (_SvgIcon = /*#__PURE__*/_jsx(SvgIcon, {})),
        onChange: onChange,
        readOnly: readOnly,
        size: muiSize[size],
        sx: {
          '&.Mui-checked': {
            '&.Mui-readOnly': {
              '.MuiSvgIcon-root': {
                backgroundColor: theme.palette.gray[25],
                borderColor: theme.palette.gray[100],
                color: theme.palette.gray[900],
                pointerEvents: 'none'
              }
            },
            '&:hover': {
              '.MuiSvgIcon-root': {
                backgroundColor: isCheckCircle ? theme.palette.blue[700] : theme.palette.blue[100],
                border: 'solid',
                borderColor: isCheckCircle ? theme.palette.blue[700] : theme.palette.blue[600],
                borderWidth: 1
              },
              background: 'none'
            },
            '.MuiSvgIcon-fontSizeSmall': _extends({
              borderRadius: 8,
              borderWidth: 1
            }, !isCheckCircle && {
              boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)'
            }, {
              height: '16px !important',
              width: '16px !important'
            }),
            '.MuiSvgIcon-root': {
              backgroundColor: isCheckCircle ? theme.palette.primary[600] : theme.palette.primary[50],
              border: 'solid',
              borderColor: theme.palette.blue[600],
              borderRadius: 10,
              borderWidth: 1,
              height: isCheckCircle ? checkCircleSizeMap[size] : 20,
              width: isCheckCircle ? checkCircleSizeMap[size] : 20
            }
          },
          '&.Mui-disabled': {
            '.MuiSvgIcon-root': {
              backgroundColor: isCheckCircle ? "".concat(theme.palette.gray[300], "!important") : theme.palette.gray[100],
              border: "1px solid ".concat(theme.palette.gray[300]),
              pointerEvents: 'none'
            }
          },
          '&.Mui-readOnly': {
            '.MuiSvgIcon-root': {
              backgroundColor: theme.palette.gray[25],
              borderColor: theme.palette.gray[100],
              pointerEvents: 'none'
            }
          },
          '&:focus-within': {
            borderRadius: 10,
            boxShadow: "0px 0px 0px 4px ".concat(theme.palette.primary[100]),
            height: 'inherit',
            width: 'inherit'
          },
          '&:hover': {
            '.MuiSvgIcon-root': {
              backgroundColor: isCheckCircle ? theme.palette.primary[700] : theme.palette.blue[100],
              border: 'solid',
              borderColor: isCheckCircle ? theme.palette.primary[700] : theme.palette.blue[600],
              borderWidth: 1
            },
            background: 'none'
          },
          '.MuiSvgIcon-fontSizeSmall': {
            borderColor: theme.palette.gray[400],
            borderRadius: '8px !important',
            borderStyle: 'solid',
            borderWidth: 1,
            height: '16px !important',
            width: '16px !important'
          },
          '.MuiSvgIcon-root': _extends({
            borderColor: theme.palette.gray[400],
            borderRadius: 10,
            borderStyle: 'solid',
            borderWidth: 1,
            height: 20,
            width: 20
          }, isCheckCircle && {
            color: 'white',
            padding: '2px'
          }),
          padding: 0
        }
      }, others, {
        "data-element": "MuiRadio",
        "data-source-file": "Radio.tsx"
      })), (label || description) && /*#__PURE__*/_jsxs(Stack, {
        gap: 0.25,
        mt: '-2px',
        children: [label && /*#__PURE__*/_jsx(Typography, {
          color: theme.palette.gray[700],
          variant: size === 'sm' ? 'text-sm-medium' : 'text-md-medium',
          children: label
        }), description && /*#__PURE__*/_jsx(Typography, {
          color: theme.palette.gray[600],
          variant: size === 'sm' ? 'text-sm-regular' : 'text-md-regular',
          children: description
        })]
      })]
    }), /*#__PURE__*/_jsx(Stack, {
      "data-element": "Stack",
      "data-source-file": "Radio.tsx",
      children: count && /*#__PURE__*/_jsxs(Typography, {
        color: theme.palette.gray[500],
        fontSize: size === 'sm' ? '12px' : '14px',
        lineHeight: '18px',
        children: ["(", count, ")"]
      })
    })]
  });
}